import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Grid,
  Popover,
  useTheme,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import dayjs from "dayjs";
import "dayjs/locale/en";
import { useDispatch, useSelector } from "react-redux";
import {
  setReservationTypeDatas,
  updateReservationGroup,
  updateReservationType,
  updateSelectedDate,
  updateTime,
  setReservationGroupDatas,
  setFreeTablesData,
  setIsBackToHoreca,
} from "../../store/slice/Addguest.slice";
import { useWidgetHeight } from "./widgetHeightContext.js";

const Calendar = ({ isOpen, handleClose, Ref }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { css } = useSelector((store) => store?.widgetSettings);
  const dateCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Date"
  )?.fields;
  const { selectedDate, dateLoading, languageWords } = useSelector(
    (store) => store?.addGuest
  );
  const { blockedDates } = useSelector((store) => store?.widgetSettings);
  const { selectedDates } = blockedDates ?? {};

  const today = dayjs();
  const [currentDate, setCurrentDate] = useState(today);
  const dispatch = useDispatch();

  // ← only onPopoverClose needed — open is handled by Widget.jsx toggle
  const { onPopoverClose } = useWidgetHeight();

  const daysOfWeek = [
    languageWords?.monday?.slice(0, 2),
    languageWords?.Tuesday?.slice(0, 2),
    languageWords?.Wednesday?.slice(0, 2),
    languageWords?.Thursday?.slice(0, 2),
    languageWords?.Friday?.slice(0, 2),
    languageWords?.Saturday?.slice(0, 2),
    languageWords?.Sunday?.slice(0, 2),
  ];

  const daysInMonth = currentDate.daysInMonth();
  const firstDayOfMonth = (currentDate.startOf("month").day() + 6) % 7;
  const isPastDay = (day) => currentDate.date(day).isBefore(today, "day");
  const isAllowedDate = (day) =>
    selectedDates?.includes(currentDate.date(day).format("YYYY-MM-DD"));
  const selectedDay =
    selectedDate?.trim() !== "" ? dayjs(selectedDate, "DD-MM-YYYY") : null;
  const isSelected = (day) =>
    selectedDay && currentDate.date(day).isSame(selectedDay, "day");
  const greatestDate = selectedDates
    ? dayjs(selectedDates[selectedDates.length - 1])
    : null;
  const isNextMonthDisabled = () =>
    selectedDates && greatestDate?.isSame(currentDate, "month");

  // Month nav doesn't affect iframe height — no notify needed
  const handleNextMonth = () => {
    if (!isNextMonthDisabled()) setCurrentDate((d) => d.add(1, "month"));
  };

  const handlePrevMonth = () => {
    if (currentDate.isAfter(today.startOf("month")))
      setCurrentDate((d) => d.subtract(1, "month"));
  };

  const handleDateSelect = (day) => {
    if (isPastDay(day) || !isAllowedDate(day)) return;
    const choosedDate = currentDate.date(day).format("DD-MM-YYYY");
    if (choosedDate === selectedDate) return;
    dispatch(setIsBackToHoreca(false));
    dispatch(setReservationTypeDatas([]));
    dispatch(updateReservationType({}));
    dispatch(setReservationGroupDatas([]));
    dispatch(updateReservationGroup({}));
    dispatch(setFreeTablesData([]));
    dispatch(updateTime(""));
    setCurrentDate(currentDate.date(day));
    dispatch(updateSelectedDate(choosedDate));
    handleClose();
    onPopoverClose(); // ← snap height back after date selected
  };

  const getTranslatedMonth = () => {
    const monthName = currentDate?.format("MMMM");
    return languageWords?.[monthName] || monthName;
  };

  const [anchorOriginVert, setAnchorOriginVert] = useState("bottom");
  const [transformOriginVert, setTransformOriginVert] = useState("top");

  useEffect(() => {
    if (!Ref?.current) return;
    const rect = Ref.current.getBoundingClientRect();
    if (rect.bottom + 400 > window.innerHeight) {
      setAnchorOriginVert("top");
      setTransformOriginVert("bottom");
    } else {
      setAnchorOriginVert("bottom");
      setTransformOriginVert("top");
    }
  }, [isOpen, Ref]);

  return (
    <Popover
      open={isOpen}
      anchorEl={Ref?.current}
      onClose={() => {
        handleClose();
        onPopoverClose();
      }} // ← backdrop click
      anchorOrigin={{ vertical: anchorOriginVert, horizontal: "center" }}
      transformOrigin={{ vertical: transformOriginVert, horizontal: "center" }}
      // No TransitionProps needed — height is static 100vh while open
      PaperProps={{
        sx: {
          width: isSmallScreen ? "92vw" : "80%",
          maxWidth: "400px",
          borderRadius: 2,
          boxShadow: 3,
          p: isSmallScreen ? 1 : 2,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        },
      }}
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      {dateLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: { md: "400px", sm: "400px", xs: "100%" },
            height: 400,
            backgroundColor: dateCss?.CalendarBackgroundColor,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ padding: isSmallScreen ? 0.5 : 2 }}>
          {/* Year nav */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: isSmallScreen ? 0.5 : 2,
              py: isSmallScreen ? 0.5 : 1,
            }}
          >
            <IconButton
              onClick={handlePrevMonth}
              disabled={currentDate.isSame(today, "month")}
              size={isSmallScreen ? "small" : "medium"}
              sx={{
                color: dateCss?.CalendarArrowColor,
                "&:hover": { color: dateCss?.CalendarArrowColorHover },
                "&:active": { color: dateCss?.CalendarArrowColorActive },
                "&.Mui-disabled": {
                  color: theme?.calender?.disabledArrowColor,
                },
              }}
            >
              <ArrowBack fontSize={isSmallScreen ? "small" : "medium"} />
            </IconButton>

            <Typography
              sx={{
                fontWeight: "bold",
                color: dateCss?.CalendarYearTextColor,
                fontSize: isSmallScreen ? "14px" : "20px",
              }}
            >
              {currentDate.format("YYYY")}
            </Typography>

            <IconButton
              onClick={handleNextMonth}
              disabled={isNextMonthDisabled()}
              size={isSmallScreen ? "small" : "medium"}
              sx={{
                color: dateCss?.CalendarArrowColor,
                "&:hover": { color: dateCss?.CalendarArrowColorHover },
                "&:active": { color: dateCss?.CalendarArrowColorActive },
                "&.Mui-disabled": {
                  color: theme?.calender?.disabledArrowColor,
                },
              }}
            >
              <ArrowForward fontSize={isSmallScreen ? "small" : "medium"} />
            </IconButton>
          </Box>

          {/* Month name */}
          <Typography
            sx={{
              mb: isSmallScreen ? 0.5 : 1,
              color: dateCss?.CalendarMonthTextColor,
              fontWeight: "bold",
              fontSize: isSmallScreen ? "16px" : "24px",
              textAlign: "center",
            }}
          >
            {getTranslatedMonth()}
          </Typography>

          {/* Day headers */}
          <Grid
            container
            spacing={isSmallScreen ? 0.5 : 1}
            mt={isSmallScreen ? 1 : 3}
          >
            {daysOfWeek.map((day, index) => (
              <Grid item xs={1.71} key={index}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color: dateCss?.CalendarDaysTextColor,
                    fontSize: isSmallScreen ? "0.65rem" : "0.9rem",
                    textAlign: "center",
                  }}
                >
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Date grid */}
          <Grid container spacing={isSmallScreen ? 0.5 : 1} sx={{ mt: 0.5 }}>
            {[...Array(firstDayOfMonth)].map((_, i) => (
              <Grid item xs={1.71} key={`e-${i}`} />
            ))}
            {[...Array(daysInMonth)].map((_, dayIndex) => {
              const day = dayIndex + 1;
              const isPast = isPastDay(day);
              const isSelectedDay = isSelected(day);
              const isActive = !isPast && isAllowedDate(day);

              return (
                <Grid item xs={1.71} key={dayIndex}>
                  <Box
                    onClick={() => handleDateSelect(day)}
                    sx={{
                      backgroundColor:
                        isSelectedDay && isActive
                          ? dateCss?.CalendarDateBackgroundColorActive
                          : !isActive
                          ? dateCss?.CalendarDateInactiveBackgroundColor
                          : dateCss?.CalendarDateBackgroundColor,
                      border: "2px dotted",
                      borderColor:
                        isPast || !isActive
                          ? dateCss?.CalendarDateInactiveBorderColor
                          : isSelectedDay && isActive
                          ? theme?.calender?.dateTextColorActive
                          : dateCss?.CalendarDateBorderColor,
                      color:
                        isSelectedDay && isActive
                          ? dateCss?.CalendarDateTextColorActive
                          : isPast || !isActive
                          ? dateCss?.CalendarDateInactiveTextColor
                          : dateCss?.CalendarDateTextColor,
                      cursor: isActive ? "pointer" : "default",
                      borderRadius: isSmallScreen ? "6px" : "8px",
                      textAlign: "center",
                      transition: "background-color 0.2s ease",
                      width: isSmallScreen ? "30px" : "40px",
                      height: isSmallScreen ? "30px" : "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mx: "auto",
                      "&:hover": {
                        backgroundColor:
                          isActive && dateCss?.CalendarDateBackgroundColorHover,
                        color: dateCss?.CalendarDateTextColorHover,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          isPast || !isActive
                            ? theme?.widget?.disableTextColor ?? "#969BA0"
                            : isSelectedDay && isActive
                            ? theme?.calender?.dateTextColorActive
                            : theme?.calender?.dateTextColor,
                        fontSize: isSmallScreen ? "12px" : "16px",
                        fontWeight: 400,
                        lineHeight: 1,
                      }}
                    >
                      {day}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Popover>
  );
};

export default Calendar;
